import { cva } from 'class-variance-authority'
import clsx from 'clsx'

import type { ComponentProps, JSX } from 'react'

/**
 * The marketing pages' four text primitives. Production ties a visual size tier to whichever tag a
 * section happened to need, not the other way round — the biggest "Title" tier renders as `h2.title`
 * on the home page, and the same tier appears on `h3` elsewhere ("the legacy pages use .title at h2
 * and h3 alike"). Tying the style to a fixed tag would force every consumer to choose between a
 * correct document outline and the correct look, so `Display`/`Title` take the tag as `as`, and
 * `Title` its visual tier as `size`.
 *
 * All four are measured on live production (`https://www.sonicjobs.com/`, 2026-09-23), not the
 * `clients/marketing-pages` source, which is reference only and already known to disagree with what
 * ships (docs/superpowers/specs/2026-09-22-s20-825-marketing-design-system-deltas.md, "the deltas
 * doc" below). Class strings must stay in this `.tsx` file — Tailwind's `@source` does not scan `.ts`.
 */

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

const heading = cva('font-sans font-normal text-ink tracking-display', {
  variants: {
    // Every tier pairs automatically to leading-display (0.96) through its own `--text-*` token —
    // no explicit `leading-*` utility needed. See theme.css and the deltas doc's collapse rows 63-67/74.
    size: {
      // The hero `<h1>`: 112px desktop measured, 42px mobile owner-directed (production 50px).
      // Production's own line-height (0.91) and tracking (-0.065em) are accepted deltas onto
      // `--text-display`/`--tracking-display` (collapse rows 74/76) — not a new normalisation.
      display: 'text-display',
      // `h2.title`: 92px desktop measured, 36px mobile owner-directed (production 42.9px). Shares
      // `--tracking-display` with the display tier (row 75: "h2.title, h3, h2" all measure -0.055em).
      h1: 'text-h1',
      // The home page's feature titles and closing call to action.
      h2: 'text-h2',
      // The measure loop's step cards.
      h3: 'text-h3',
    },
  },
})

type HeadingProps = ComponentProps<'h1'> & { as?: HeadingTag }

function Heading({
  size,
  as,
  className,
  ...props
}: HeadingProps & { size: 'display' | 'h1' | 'h2' | 'h3' }): JSX.Element {
  const Tag = as ?? (size === 'display' ? 'h1' : 'h2')

  return <Tag {...props} className={heading({ size, className })} />
}

/** The hero headline. Measured `text-ink`, `text-display`, `tracking-display`; defaults to `<h1>`. */
export function Display(props: HeadingProps): JSX.Element {
  return <Heading size="display" {...props} />
}

/**
 * The small caps label above a headline. Measured on production's `.eyebrow`: `13px` (`--text-sm`,
 * exact), weight 500, `rgb(98,102,108)` — exactly `--color-ink-muted` — and `letter-spacing: normal`.
 * The legacy stylesheet's `0.11em` tracking is NOT what production renders (verified live): no
 * `tracking-*` utility is applied here, because none is measured.
 */
export function Eyebrow({ className, ...props }: ComponentProps<'p'>): JSX.Element {
  return <p {...props} className={clsx('font-sans font-medium uppercase text-ink-muted text-sm', className)} />
}

/**
 * The intro paragraph under a `Display` or `Title`. Measured on production's `p.lede`: `19px` desktop
 * (the `--text-lead` clamp's own upper end), `rgba(13,13,13,.66)` — exactly `--color-ink-secondary` —
 * and `-0.015em` tracking — exactly `--tracking-lead`. Production's own leading measures 1.5, which
 * `--text-lead`'s pair (1.55) already accepts as a delta (collapse row 70); no override here.
 */
export function Lede({ className, ...props }: ComponentProps<'p'>): JSX.Element {
  return (
    <p {...props} className={clsx('font-sans font-normal text-ink-secondary text-lead tracking-lead', className)} />
  )
}

/**
 * A section headline. Measured `text-ink`, `text-h1`, `tracking-display`; defaults to `<h2>`, matching
 * production's `h2.title`, but any level can be chosen — see the module docblock. `size` picks a
 * smaller tier of the same heading scale.
 */
export function Title({ size = 'h1', ...props }: HeadingProps & { size?: 'h1' | 'h2' | 'h3' }): JSX.Element {
  return <Heading size={size} {...props} />
}
